/* eslint-disable security/detect-object-injection */
import React, {useEffect, useState, useCallback, useMemo} from 'react';
import {useLocation, useSearchParams} from 'react-router-dom';
import {Box} from '@mui/material';
import {useSelector} from 'react-redux';
import StandardReports from '../../StandardReports';
import {
  mergeFormatDataIntoConfig,
  reportApiMap,
} from '../../common/ReportHelper';
import {ISelectItem} from '../../../../../shared/types';
import {Reports, RightPanelConfig} from '../../config/reportTypes';
import {getExportDataSource} from '../../../../../hooks/reporting/useReporting';
import {SourceItem} from '../../../tally/selectSourceContainer/ArrSourceSelectedContainer';
import {RootState} from '../../../../../redux/store';
import {SourceTypeCode} from '../../../../../shared/constants';
import {ASSIGNMENT_EXPORT_SECTION_ID} from '../../component/left-panel/components/ArrSourceSelectedSection';
import './DataExportContainer.scss';

type SelectionCriteria = {
  sourceType?: string;
  sourceID?: string[] | string;
  style?: string;
  field?: string;
  text?: string;
  newLine?: string;
  dateStyle?: string;
  fromDateTime?: string | Date;
  toDateTime?: string | Date;
  selectedReviewers?: Array<string | {userName?: string; label?: string}>;
  exportFields?: Array<{index: number; type: string; fieldId: string}>;
  toggelExportObjectValue?: boolean;
};

function extractSavedSelectionCriteria(
  resp: unknown
): SelectionCriteria | null {
  if (!resp || typeof resp !== 'object') return null;
  const r = resp as {
    savedSelectionCriteria?: unknown;
    selectionCriteria?: unknown;
    exportModelData?: {
      savedSelectionCriteria?: unknown;
      selectionCriteria?: unknown;
    };
  };
  const asCriteria = (candidate: unknown): SelectionCriteria | null => {
    if (!candidate || typeof candidate !== 'object') return null;
    return candidate as SelectionCriteria;
  };
  return (
    asCriteria(r.savedSelectionCriteria) ??
    asCriteria(r.selectionCriteria) ??
    asCriteria(r.exportModelData?.savedSelectionCriteria) ??
    asCriteria(r.exportModelData?.selectionCriteria) ??
    null
  );
}

function extractExportFields(resp: any): any[] | null {
  const model = resp?.exportModelData;
  const modelEf = Array.isArray(model)
    ? model?.[0]?.exportFields
    : model?.exportFields;
  const ef =
    resp?.selectedSourceDetails?.exportFields ??
    resp?.exportFields ??
    resp?.selectionCriteria?.exportFields ??
    resp?.savedSelectionCriteria?.exportFields ??
    resp?.savedSelectionCriteria?.selectionCriteria?.exportFields ??
    modelEf ??
    null;
  return Array.isArray(ef) ? ef : null;
}

function injectExportFieldsIntoExportModelData(
  resp: any,
  exportFields: any[] | null
): any {
  if (!exportFields || !exportFields.length) return resp;
  const model = resp?.exportModelData;
  if (Array.isArray(model)) {
    const first = model[0] ?? {};
    const updatedFirst = {
      ...first,
      exportFields: first.exportFields ?? exportFields,
    };
    return {...resp, exportModelData: [updatedFirst, ...model.slice(1)]};
  }
  if (model && typeof model === 'object') {
    return {
      ...resp,
      exportModelData: {
        ...model,
        exportFields: model.exportFields ?? exportFields,
      },
    };
  }
  return resp;
}

function getExportFieldsFromDynamicData(d: any): any[] | null {
  const model = d?.exportModelData;
  const ef = Array.isArray(model)
    ? model?.[0]?.exportFields
    : model?.exportFields;
  return Array.isArray(ef) ? ef : null;
}

interface ReportConfig {
  title: string;
  showInfoIcon?: boolean;
  config: Reports;
}

const DEFAULT_OPTION: ISelectItem = {
  value: 'currentLoggedIn',
  label: 'Currently Logged-In Users',
};

const DataExportContainer: React.FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const userRole = useSelector(
    (state: RootState) => state?.authenticatedUser?.user?.roleName
  );

  const {item} = location.state || {};

  const tallyExport =
    item.navigationSourceType ===
    (SourceTypeCode.TALLY || SourceTypeCode.SUBTALLY)
      ? item
      : null;

  const advbatmgmtrptExport =
    item.navigationSourceType === SourceTypeCode.ADVBATMGMTRPT ? item : null;

  const sessionSearchExport =
    item.navigationSourceType === SourceTypeCode.SESSIONSEARCH ? item : null;

  const timeLineExport = item.navigationSourceType === 'TIMELINE' ? item : null;

  const docExplorerExport =
    item.navigationSourceType === SourceTypeCode.DOCEXPLORER ? item : null;

  const searchTermExport =
    item.navigationSourceType === SourceTypeCode.SEARCHTERM ? item : null;

  const doclistExport =
    item.navigationSourceType === SourceTypeCode.DOCLIST ? item : null;

  const savedSearchExport =
    item.navigationSourceType === SourceTypeCode.SAVEDSEARCH ? item : null;

  const assignmentExport =
    item.navigationSourceType === SourceTypeCode.ASSIGNMENT ? item : null;

  const deepLinkItem = useMemo(() => {
    if (item) return item;
    const reportIdParam = searchParams.get('ReportID');
    const reportLabelParam = searchParams.get('ReportLabel');
    const reportTypeCodeParam = searchParams.get('ReportTypeCode') ?? 'CDR011';
    return {
      id: reportIdParam ? Number(reportIdParam) : undefined,
      label: reportLabelParam ?? 'ExportData',
      reportTypeCode: reportTypeCodeParam,
    };
  }, [item, searchParams]);
  const effectiveItem = item ?? deepLinkItem;
  const pathSegment = location.pathname.split('/')[2];
  const reportKey = pathSegment === 'export' ? 'ExportData' : pathSegment;
  const [reportConfig, setReportConfig] = useState<ReportConfig | null>(null);
  const [dynamicData, setDynamicData] = useState<any>(null);
  const [selectedOption, setSelectedOption] = useState<ISelectItem | null>(
    DEFAULT_OPTION
  );
  const [gridData, setGridData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [resolvedParams, setResolvedParams] =
    useState<SelectionCriteria | null>(null);
  const [savedSelectionCriteria, setSavedSelectionCriteria] =
    useState<SelectionCriteria | null>(null);
  const dataExportViewSources = dynamicData?.exportModelData;
  const {mutateAsync: fetchExportView} = getExportDataSource();
  const RIGHT_PANEL_HEADER_CONFIG: RightPanelConfig = {
    showSaveIcon: true,
  };

  useEffect(() => {
    const init = async () => {
      try {
        const reportId = effectiveItem?.id;
        const reportLabel = effectiveItem?.label ?? 'ExportData';
        const payload = {
          reportID: reportId,
          ReportID: reportId,
          reportLabel: reportLabel,
          ReportLabel: reportLabel,
          share: '',
          Share: '',
          tempDataRedirectFrom: '',
          tempDataSourceList: [[]],
          tempDataSourceName: '',
          isPopup: true,
        };
        setLoading(true);
        const viewResponse = await fetchExportView(payload);
        setLoading(false);
        if (viewResponse) {
          const saved = extractSavedSelectionCriteria(viewResponse);
          const exportFields =
            extractExportFields(viewResponse) ?? saved?.exportFields ?? null;
          const injected = injectExportFieldsIntoExportModelData(
            viewResponse,
            exportFields
          );
          setDynamicData(injected);
          if (saved) {
            const savedWithExportFields = {
              ...saved,
              exportFields: saved.exportFields ?? exportFields ?? undefined,
            };
            setSavedSelectionCriteria(savedWithExportFields);
            setResolvedParams({
              sourceType: saved.sourceType,
              sourceID: saved.sourceID,
              style: saved.style,
              field: saved.field,
              text: saved.text,
              newLine: saved.newLine,
              dateStyle: saved.dateStyle,
              fromDateTime: saved.fromDateTime,
              toDateTime: saved.toDateTime,
              selectedReviewers: saved.selectedReviewers,
              exportFields: saved.exportFields ?? exportFields ?? undefined,
              toggelExportObjectValue: saved.toggelExportObjectValue,
            });
            setDynamicData((prev: any) => ({
              ...(prev ?? injected),
              savedSelectionCriteria: savedWithExportFields,
            }));
          } else if (injected?.selectedSourceDetails?.sourceType) {
            setResolvedParams({
              sourceType: injected?.selectedSourceDetails?.sourceType,
              sourceID: injected?.selectedSourceDetails?.sourceID,
              exportFields: exportFields ?? undefined,
            });
          }
        }
      } catch (error) {
        setLoading(false);
        console.error('Error initializing export view:', error);
        setDynamicData((prev: any) => ({
          ...(prev ?? {}),
          error: 'Failed to load saved report. Please try again later.',
        }));
      }
    };
    init();
  }, [fetchExportView, effectiveItem]);

  useEffect(() => {
    if (dynamicData?.selectedSourceDetails?.sourceType) {
      setSelectedOption({
        label: dynamicData?.selectedSourceDetails?.sourceType,
        value: dynamicData?.selectedSourceDetails?.sourceType,
      });
    }
  }, [dynamicData?.selectedSourceDetails]);

  const loadReportConfig = useCallback(async () => {
    if (!reportKey) return;
    try {
      const data = await import(`../../config/${reportKey}.json`);
      setReportConfig(data.report);
    } catch (error) {
      console.error('Error loading JSON config:', error);
    }
  }, [reportKey, selectedOption]);

  const loadDynamicData = useCallback(async () => {
    const apiFn = reportApiMap[reportKey];
    if (apiFn) {
      try {
        const data = await apiFn();
        setDynamicData((prev: any) => {
          const prevEf = getExportFieldsFromDynamicData(prev);
          const next = {...(prev ?? {}), ...(data ?? {})};
          if (prevEf) {
            const model = next?.exportModelData;
            if (Array.isArray(model)) {
              const first = model[0] ?? {};
              next.exportModelData = [
                {...first, exportFields: first.exportFields ?? prevEf},
                ...model.slice(1),
              ];
            } else if (model && typeof model === 'object') {
              next.exportModelData = {
                ...model,
                exportFields: model.exportFields ?? prevEf,
              };
            } else if (prev?.exportModelData) {
              next.exportModelData = prev.exportModelData;
            }
          }
          return next;
        });
      } catch (error) {
        console.error('Error fetching API data:', error);
      }
    }
  }, [reportKey]);

  useEffect(() => {
    loadReportConfig();
    loadDynamicData();
  }, [loadReportConfig, loadDynamicData]);

  const sources: SourceItem[] = useMemo(() => {
    if (!dataExportViewSources) return [];
    const src: any = Array.isArray(dataExportViewSources)
      ? dataExportViewSources[0]
      : dataExportViewSources;
    const baseSources: SourceItem[] = [
      {
        id: 'securityGroups',
        label: 'Security Groups',
        data: src?.securityGroupList ?? [],
      },
      {id: 'reviewers', label: 'Reviewers', data: src?.reviewersList ?? []},
      {id: 'searches', label: 'Searches', data: src?.searchTreeList ?? []},
      {
        id: 'assignments',
        label: 'Assignments',
        data: src?.assignmentTreeList ?? [],
      },
      {
        id: 'projects',
        label: 'Projects',
        data: src?.projectList ?? [],
      },
      {id: 'folders', label: 'Folders', data: src?.folderTreeList ?? []},
    ];
    return baseSources;
  }, [dataExportViewSources, userRole]);

  if (!reportConfig) return <div data-testid={`data-export-container-loading`}>Loading...</div>;
  const updatedConfig = dynamicData
    ? mergeFormatDataIntoConfig(reportConfig.config, dynamicData)
    : reportConfig.config;

  const handleFilterChange = (values: Record<string, any>) => {
    if (!values || Object.keys(values).length === 0) {
      setSelectedOption(null);
      setGridData(null);
    }
  };

  const getInitialSelectedSources = () => {
    const sources: Record<string, any[]> = {};
    if (docExplorerExport) {
      sources['docexplorerexport'] = [docExplorerExport];
    }

    if (advbatmgmtrptExport) {
      sources['advbatmgmtrptexport'] = [advbatmgmtrptExport];
    }

    if (timeLineExport) {
      sources['timelineexport'] = [timeLineExport];
    }

    if (sessionSearchExport) {
      sources['sessionsearchexport'] = [sessionSearchExport];
    }

    if (tallyExport) {
      sources['tallyexport'] = [tallyExport];
    }

    if (searchTermExport) {
      sources['searchtermexport'] = [searchTermExport];
    }

    if (doclistExport) {
      sources['doclistexport'] = [doclistExport];
    }

    if (savedSearchExport) {
      sources['savedsearchexport'] = [savedSearchExport];
    }

    if (assignmentExport) {
      sources[ASSIGNMENT_EXPORT_SECTION_ID] = [assignmentExport];
    }
    return sources;
  };

  return (
    <Box className="export-report-container">
      <StandardReports
        isExportDataReport={true}
        title={
          location?.state?.fromReportLanding
            ? (location?.state?.item?.label ?? '')
            : (reportConfig?.title ?? '')
        }
        showInfoIcon={true}
        infoModalTitle="Data Export Information"
        infoContent={'some'}
        reportsConfig={updatedConfig}
        selectedOption={selectedOption}
        onOptionChange={setSelectedOption}
        data={dynamicData}
        loading={loading}
        columns={[]}
        mapRows={gridData}
        sources={sources}
        initialSelectedSources={getInitialSelectedSources()}
        onFilterChange={handleFilterChange}
        rightPanelHeaderConfig={RIGHT_PANEL_HEADER_CONFIG}
        resolvedReportParams={resolvedParams ?? undefined}
        saveReportConfig={{savedSelectionCriteria}}
        disableSourceSelection={item.sourceDisable ?? false}
        fixedRightPanelWidth={true}
      />
    </Box>
  );
};

export default DataExportContainer;
